"use client";

import React, { useState } from "react";
import type { AccountAnalytics } from "@/vybe-trader-client/trade-history/accountAnalytics";
import { formatDurationShort } from "@/vybe-trader-client/trade-history/accountAnalytics";
import {
  AnalyticsCard,
  AnalyticsCardHeader,
  AnalyticsTab,
  DurationScatter,
  GroupedWinLossBars,
  StatRow,
  WinLossSplitBar,
  WEEKDAY_LABELS,
  fmtMoney,
  fmtPct,
  fmtPips,
  fmtRatio,
  fmtSignedMoney,
  fmtSignedPct,
  pnlClass,
} from "./analyticsShared";

type AdvTab = "trades" | "summary" | "hourly" | "daily" | "risk" | "duration";

const summaryTh =
  "px-2 py-1 text-left text-[10px] font-medium uppercase tracking-wide text-muted-foreground whitespace-nowrap";
const summaryTd =
  "px-2 py-0.5 font-mono tabular-nums text-[11px] whitespace-nowrap";

/** Advanced statistics — Trades · Summary · Hourly · Daily · Risk of Ruin · Duration. */
export default function AnalyticsAdvancedCard({
  analytics,
  currency,
}: {
  analytics: AccountAnalytics;
  currency: string;
}) {
  const [tab, setTab] = useState<AdvTab>("trades");
  const t = analytics.trades;

  return (
    <AnalyticsCard>
      <AnalyticsCardHeader>
        <span className="mr-1">Advanced statistics</span>
        <AnalyticsTab active={tab === "trades"} onClick={() => setTab("trades")}>
          Trades
        </AnalyticsTab>
        <AnalyticsTab
          active={tab === "summary"}
          onClick={() => setTab("summary")}
        >
          Summary
        </AnalyticsTab>
        <AnalyticsTab active={tab === "hourly"} onClick={() => setTab("hourly")}>
          Hourly
        </AnalyticsTab>
        <AnalyticsTab active={tab === "daily"} onClick={() => setTab("daily")}>
          Daily
        </AnalyticsTab>
        <AnalyticsTab active={tab === "risk"} onClick={() => setTab("risk")}>
          Risk of Ruin
        </AnalyticsTab>
        <AnalyticsTab
          active={tab === "duration"}
          onClick={() => setTab("duration")}
        >
          Duration
        </AnalyticsTab>
      </AnalyticsCardHeader>

      {tab === "trades" && (
        <div className="grid grid-cols-1 gap-x-6 p-2 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <StatRow label="Trades" value={t.totalTrades} />
            <StatRow
              label="Profitability"
              value={
                <span className="inline-flex items-center gap-2">
                  <span className="text-blue-400">
                    {t.winCount}
                  </span>
                  <WinLossSplitBar winPct={t.winRatePct} />
                  <span className="text-red-400">{t.lossCount}</span>
                </span>
              }
            />
            <StatRow label="Win rate" value={fmtPct(t.winRatePct, 1)} />
            <StatRow
              label="Pips"
              value={fmtPips(t.pipsTotal)}
              valueClass={pnlClass(t.pipsTotal)}
            />
            <StatRow
              label="Average win"
              value={`${fmtPips(t.avgWinPips)} pips / ${fmtMoney(t.avgWin, currency)}`}
            />
            <StatRow
              label="Average loss"
              value={`${fmtPips(t.avgLossPips)} pips / -${fmtMoney(t.avgLoss, currency)}`}
            />
            <StatRow label="Lots" value={t.lotsTotal.toFixed(2)} />
            <StatRow
              label="Commissions"
              value={fmtMoney(t.commissionTotal, currency)}
            />
          </div>
          <div>
            <StatRow
              label="Longs won"
              value={`(${t.longs.won}/${t.longs.total}) ${fmtPct(t.longs.winRatePct, 0)}`}
            />
            <StatRow
              label="Shorts won"
              value={`(${t.shorts.won}/${t.shorts.total}) ${fmtPct(t.shorts.winRatePct, 0)}`}
            />
            <StatRow
              label="Best trade"
              value={fmtSignedMoney(t.bestTrade, currency)}
              valueClass={pnlClass(t.bestTrade)}
            />
            <StatRow
              label="Worst trade"
              value={fmtSignedMoney(t.worstTrade, currency)}
              valueClass={pnlClass(t.worstTrade)}
            />
            <StatRow
              label="Best trade (pips)"
              value={fmtPips(t.bestTradePips)}
              valueClass={pnlClass(t.bestTradePips)}
            />
            <StatRow
              label="Worst trade (pips)"
              value={fmtPips(t.worstTradePips)}
              valueClass={pnlClass(t.worstTradePips)}
            />
            <StatRow
              label="Avg trade length"
              value={formatDurationShort(t.avgTradeLengthMs)}
            />
            <StatRow
              label="Swap"
              value={fmtSignedMoney(t.swapTotal, currency)}
            />
          </div>
          <div>
            <StatRow
              label="Profit factor"
              value={fmtRatio(
                t.profitFactor === Infinity ? Infinity : t.profitFactor
              )}
            />
            <StatRow
              label="Standard deviation"
              value={fmtMoney(t.stdDev, currency)}
            />
            <StatRow
              label="Sharpe (per trade)"
              value={t.sharpe === null ? "—" : t.sharpe.toFixed(2)}
            />
            <StatRow
              label="Z-score (probability)"
              value={
                t.zScore === null
                  ? "—"
                  : `${t.zScore.toFixed(2)} (${fmtPct(t.zProbabilityPct, 1)})`
              }
            />
            <StatRow
              label="Expectancy"
              value={`${fmtPips(t.expectancyPips)} pips / ${fmtSignedMoney(t.expectancy, currency)}`}
              valueClass={pnlClass(t.expectancy)}
            />
            <StatRow
              label="AHPR"
              value={fmtSignedPct(t.ahprPct, 3)}
              valueClass={pnlClass(t.ahprPct)}
            />
            <StatRow
              label="GHPR"
              value={fmtSignedPct(t.ghprPct, 3)}
              valueClass={pnlClass(t.ghprPct)}
            />
          </div>
        </div>
      )}

      {tab === "summary" && (
        <div className="overflow-x-auto">
          {analytics.symbolSummary.length === 0 ? (
            <EmptyPane />
          ) : (
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-border">
                  <th className={summaryTh}>Symbol</th>
                  <th className={summaryTh}>Side</th>
                  <th className={`${summaryTh} text-right`}>Trades</th>
                  <th className={`${summaryTh} text-right`}>Pips</th>
                  <th className={`${summaryTh} text-right`}>Profit</th>
                  <th className={`${summaryTh} text-right`}>Won %</th>
                  <th className={`${summaryTh} text-right`}>Lost %</th>
                </tr>
              </thead>
              <tbody>
                {analytics.symbolSummary.map((g) =>
                  (
                    [
                      ["Longs", g.long, "text-blue-400"],
                      ["Shorts", g.short, "text-red-400"],
                      ["Total", g.total, "text-foreground"],
                    ] as const
                  ).map(([label, side, sideClass], i) => (
                    <tr
                      key={`${g.symbol}-${label}`}
                      className={`h-7 hover:bg-accent ${
                        label === "Total"
                          ? "border-b border-border"
                          : "border-b border-border/40"
                      }`}
                    >
                      <td className={`${summaryTd} font-semibold`}>
                        {i === 0 ? g.symbol : ""}
                      </td>
                      <td className={`${summaryTd} ${sideClass}`}>{label}</td>
                      <td className={`${summaryTd} text-right`}>
                        {side.trades}
                      </td>
                      <td
                        className={`${summaryTd} text-right ${pnlClass(side.pips)}`}
                      >
                        {fmtPips(side.pips)}
                      </td>
                      <td
                        className={`${summaryTd} text-right ${pnlClass(side.profit)}`}
                      >
                        {fmtSignedMoney(side.profit)}
                      </td>
                      <td className={`${summaryTd} text-right`}>
                        {fmtPct(side.wonPct, 0)}
                      </td>
                      <td className={`${summaryTd} text-right`}>
                        {fmtPct(side.lostPct, 0)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "hourly" && (
        <GroupedWinLossBars
          buckets={analytics.hourly}
          labels={(key) => String(key)}
        />
      )}

      {tab === "daily" && (
        <GroupedWinLossBars
          buckets={analytics.weekday}
          labels={(key) => WEEKDAY_LABELS[key] ?? String(key)}
        />
      )}

      {tab === "risk" && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-border">
                <th className={summaryTh}>Loss size</th>
                <th className={`${summaryTh} text-right`}>
                  Probability of loss
                </th>
                <th className={`${summaryTh} text-right`}>
                  Consecutive losing trades
                </th>
              </tr>
            </thead>
            <tbody>
              {analytics.riskOfRuin.map((row) => (
                <tr
                  key={row.lossPct}
                  className="h-7 border-b border-border/40 hover:bg-accent"
                >
                  <td className={summaryTd}>{row.lossPct}%</td>
                  <td className={`${summaryTd} text-right`}>
                    {row.probabilityPct === null
                      ? "—"
                      : row.probabilityPct < 0.01 && row.probabilityPct > 0
                        ? "< 0.01%"
                        : fmtPct(row.probabilityPct)}
                  </td>
                  <td className={`${summaryTd} text-right`}>
                    {row.consecutiveLosses ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-2 py-1.5 text-[10px] text-muted-foreground">
            Derived from the window's win rate and average win/loss — a
            statistical estimate, not a guarantee.
          </p>
        </div>
      )}

      {tab === "duration" && (
        <div>
          <DurationScatter
            points={analytics.durationScatter}
            formatDuration={formatDurationShort}
          />
          <p className="px-2 pb-2 text-[10px] text-muted-foreground">
            Last {analytics.durationScatter.length} trades · winners{" "}
            <span className="text-blue-400">blue</span> · losers{" "}
            <span className="text-red-400">red</span>
          </p>
        </div>
      )}
    </AnalyticsCard>
  );
}

function EmptyPane() {
  return (
    <div className="flex h-16 items-center justify-center text-[11px] text-muted-foreground">
      No trades in this scope
    </div>
  );
}
